import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import ComplaintStatusBadge from "@/components/ComplaintStatusBadge";
import SeverityBadge from "@/components/SeverityBadge";
import { api, getAxiosErrorMessage, API_BASE } from "@/lib/api";
import { Plus, Search, Download, Loader2, AlertOctagon, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { isCommercial } from "@/lib/roles";
import TypeBar from "@/components/TypeBar";
import { TYPE_LABELS, TYPE_ORDER, typesCreables } from "@/lib/complaintTypes";
import { logoConcerne, nomConcerne } from "@/lib/concerned";
import ConcernedFilter from "@/components/ConcernedFilter";



// Libellés lisibles dans un tableur : la valeur technique n'y a pas sa place.
const STATUT_EXPORT = { ouverte: "Ouvert", cloturee: "Clôturé" };

function EntityLogo({ fileId, name, className = "h-7 w-7" }) {
  if (!fileId) return null;
  return (
    <img
      src={`${API_BASE}/files/${fileId}/download`}
      alt={name || ""}
      className={`${className} rounded-md object-contain bg-white border border-slate-100`}
    />
  );
}

export default function Complaints() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [type, setType] = useState("all");
  const [severity, setSeverity] = useState("all");
  const [assignedTo, setAssignedTo] = useState("all");
  const [createdBy, setCreatedBy] = useState("all");
  const [concernedId, setConcernedId] = useState(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [annuaire, setAnnuaire] = useState([]);
  const [compteurs, setCompteurs] = useState({ total: 0, by_type: [] });
  const [createOpen, setCreateOpen] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const commercial = isCommercial(user);

  const fetchList = async () => {
    setLoading(true);
    try {
      // Tous les critères partent au serveur : un filtrage côté navigateur ne
      // verrait jamais ce qui se trouve au-delà des éléments déjà chargés.
      const params = {};
      if (status !== "all") params.status = status;
      if (!commercial && type !== "all") params.type = type;
      if (severity !== "all") params.severity = severity;
      if (!commercial && assignedTo !== "all") params.assigned_to = assignedTo;
      if (!commercial && createdBy !== "all") params.created_by = createdBy;
      if (concernedId) params.concerned_id = concernedId;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (search) params.search = search;

      // Les compteurs partagent EXACTEMENT les filtres de la liste, sauf le
      // type : sinon les nombres afficheraient autre chose que ce que la liste
      // montre. Le backend ignore `type` sur cette route.
      const { type: _ignore, ...filtresCompteurs } = params;
      const [liste, comptes] = await Promise.all([
        api.get("/complaints", { params }),
        api.get("/complaints/type-counts", { params: filtresCompteurs }),
      ]);
      setItems(liste.data);
      setCompteurs(comptes.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Anti-rebond sur la saisie : sans lui, chaque frappe déclencherait une
    // requête. 350 ms est assez court pour paraître immédiat.
    const minuteur = setTimeout(fetchList, search ? 350 : 0);
    return () => clearTimeout(minuteur);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, type, severity, assignedTo, createdBy, concernedId, dateFrom, dateTo, search]);

  useEffect(() => {
    if (commercial) return;
    api.get("/users/assignable")
      .then(({ data }) => setAnnuaire(data))
      .catch(() => setAnnuaire([]));
  }, [commercial]);

  // La liste affichée est celle que le serveur a renvoyée : plus de second
  // filtrage local, qui masquait des résultats au-delà de la page courante.
  const filtered = items;

  const exportCSV = () => {
    // L'ordre suit celui de l'écran, statut en tête : un export dont les colonnes
    // ne sont pas celles qu'on vient de lire oblige à les rechercher une à une.
    const header = ["Statut", "Numéro", "Date", "Type", "Sous-type", "Concerné", "Réf. produit", "Créé par", "Gravité", "Description"];
    const rows = filtered.map((c) => [
      STATUT_EXPORT[c.status] || c.status,
      c.number, (c.created_at || "").slice(0, 10), TYPE_LABELS[c.type] || c.type, c.sub_type || "",
      nomConcerne(c), c.product_ref || "", c.created_by_name || "",
      c.severity, (c.description || "").replace(/\n/g, " "),
    ]);
    const csv = [header, ...rows]
      .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(";"))
      .join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `litiges-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success("Export CSV généré");
  };

  return (
    <>
      <Header
        title={commercial ? "Mes litiges clients" : "Litiges"}
        subtitle={commercial ? "Accès réservé aux litiges clients" : "Point d'entrée unique des incidents et litiges"}
        actions={
          <Button
            variant="secondary"
            onClick={exportCSV}
            className="bg-white/10 hover:bg-white/20 text-white border-0"
            data-testid="export-csv-btn"
          >
            <Download size={16} className="mr-1.5" />
            Exporter
          </Button>
        }
      />
      <main className="flex-1 p-4 sm:p-6 lg:p-8 space-y-5">
        <TypeBar
          value={type}
          onChange={setType}
          counts={Object.fromEntries(compteurs.by_type.map((e) => [e.type, e.count]))}
          total={compteurs.total}
          // Le commercial ne voit que les litiges client : lui présenter les
          // autres typologies à zéro lui suggérerait des dossiers inaccessibles.
          types={commercial ? ["client"] : TYPE_ORDER}
        />
        <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] p-5 space-y-4">
          <div className="flex flex-col lg:flex-row gap-3 items-stretch lg:items-center">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher un numéro, description, tag, réf. produit…"
                className="pl-9 rounded-xl h-10 border-slate-200"
                data-testid="complaints-search-input"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {!commercial ? (
                <>
                  <Select value={assignedTo} onValueChange={setAssignedTo}>
                    <SelectTrigger className="w-[170px] rounded-xl border-slate-200" data-testid="filter-assigned">
                      <SelectValue placeholder="Responsable" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous responsables</SelectItem>
                      {annuaire.map((u) => (
                        <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={createdBy} onValueChange={setCreatedBy}>
                    <SelectTrigger className="w-[160px] rounded-xl border-slate-200" data-testid="filter-author">
                      <SelectValue placeholder="Auteur" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous auteurs</SelectItem>
                      {annuaire.map((u) => (
                        <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </>
              ) : null}
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="h-10 rounded-xl border border-slate-200 px-3 text-sm text-slate-700"
                aria-label="Du"
                data-testid="filter-date-from"
              />
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="h-10 rounded-xl border border-slate-200 px-3 text-sm text-slate-700"
                aria-label="Au"
                data-testid="filter-date-to"
              />
              <ConcernedFilter
                value={concernedId}
                onChange={setConcernedId}
                commercial={commercial}
              />
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="w-[150px] rounded-xl border-slate-200" data-testid="filter-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous statuts</SelectItem>
                  <SelectItem value="ouverte">Ouverts</SelectItem>
                  <SelectItem value="cloturee">Clôturés</SelectItem>
                </SelectContent>
              </Select>
              <Select value={severity} onValueChange={setSeverity}>
                <SelectTrigger className="w-[140px] rounded-xl border-slate-200" data-testid="filter-severity">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toute gravité</SelectItem>
                  <SelectItem value="faible">Faible</SelectItem>
                  <SelectItem value="moyenne">Moyenne</SelectItem>
                  <SelectItem value="critique">Critique</SelectItem>
                </SelectContent>
              </Select>
              <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogTrigger asChild>
                  <Button
                    className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl shadow-sm hover:shadow-md h-10"
                    data-testid="new-complaint-btn"
                  >
                    <Plus size={16} className="mr-1.5" /> Nouveau litige
                  </Button>
                </DialogTrigger>
                <ComplaintDialog
                  commercial={commercial}
                  onClose={() => setCreateOpen(false)}
                  onCreated={(c) => {
                    setCreateOpen(false);
                    fetchList();
                    navigate(`/litiges/${c.id}`);
                  }}
                />
              </Dialog>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-testid="complaints-table">
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-slate-500">
              <AlertOctagon size={32} className="mx-auto text-slate-300 mb-3" strokeWidth={1.5} />
              Aucun litige trouvé.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50/60 border-b border-slate-100">
                  <tr>
                    {/* Le statut ouvre la ligne : la question posée en arrivant sur
                        cet écran est « qu'est-ce qui est encore ouvert ? », pas
                        « quel numéro porte ce dossier ». */}
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Statut</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Numéro</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Date</th>
                    {!commercial && <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Type</th>}
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Concerné</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Créé par</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Description</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Gravité</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {filtered.map((c) => {
                    const logoId = logoConcerne(c);
                    return (
                      <tr
                        key={c.id}
                        onClick={() => navigate(`/litiges/${c.id}`)}
                        className="hover:bg-blue-50/40 cursor-pointer transition-colors"
                        data-testid={`complaint-row-${c.id}`}
                      >
                        <td className="px-4 py-4 whitespace-nowrap">
                          <ComplaintStatusBadge status={c.status} />
                        </td>
                        <td className="px-6 py-4 text-sm font-bold text-slate-900 font-heading whitespace-nowrap">
                          {c.number}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600 whitespace-nowrap">
                          {(c.created_at || "").slice(0, 10)}
                        </td>
                        {!commercial && (
                          <td className="px-6 py-4 text-sm text-slate-600 whitespace-nowrap">
                            {TYPE_LABELS[c.type] || c.type}
                          </td>
                        )}
                        <td className="px-6 py-4 text-sm text-slate-700 whitespace-nowrap font-medium">
                          <div className="flex items-center gap-2">
                            <EntityLogo fileId={logoId} name={nomConcerne(c)} />
                            <span>{nomConcerne(c)}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-700 whitespace-nowrap">
                          {c.created_by_name || "—"}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600 max-w-md truncate">
                          {c.description}
                        </td>
                        <td className="px-6 py-4"><SeverityBadge severity={c.severity} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </>
  );
}

function ComplaintDialog({ onClose, onCreated, commercial = false }) {
  const { user } = useAuth();
  // Typologies que le rôle a réellement le droit de créer. Miroir de la matrice
  // backend, qui reste l'autorité : cette liste évite seulement de proposer une
  // option aboutissant à un 403 — une règle de gestion ne doit pas se présenter
  // à l'utilisateur comme une panne.
  const typesAutorises = typesCreables(user);
  const [form, setForm] = useState({
    type: typesCreables(user)[0] || "client",
    sub_type: "",
    client_id: "none",
    supplier_id: "none",
    carrier_id: "none",
    product_ref: "",
    description: "",
    severity: "moyenne",
    tags: "",
  });
  const [clients, setClients] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [duplicates, setDuplicates] = useState([]);
  const [showDuplicateAlert, setShowDuplicateAlert] = useState(false);

  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data));
    if (!commercial) {
      api.get("/suppliers", { params: { kind: "fournisseur" } })
        .then((r) => setSuppliers(r.data)).catch(() => {});
      // Les transporteurs partagent la collection ; `les_deux` figure dans
      // les deux listes puisqu'il exerce les deux roles.
      Promise.all([
        api.get("/suppliers", { params: { kind: "transporteur" } }),
        api.get("/suppliers", { params: { kind: "les_deux" } }),
      ]).then(([a, b]) => setCarriers([...a.data, ...b.data]
        .sort((x, y) => x.name.localeCompare(y.name)))).catch(() => {});
    }
  }, [commercial]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const buildPayload = (force) => ({
    type: form.type,
    sub_type: form.sub_type,
    client_id: form.type === "client" && form.client_id !== "none" ? form.client_id : null,
    supplier_id: form.type === "fournisseur" && form.supplier_id !== "none" ? form.supplier_id : null,
    carrier_id: form.type === "transporteur" && form.carrier_id !== "none" ? form.carrier_id : null,
    product_ref: form.product_ref,
    description: form.description,
    severity: form.severity,
    tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
    force: !!force,
  });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Pre-check duplicates
      const { data: check } = await api.post("/complaints/check-duplicate", {
        type: form.type,
        client_id: form.type === "client" && form.client_id !== "none" ? form.client_id : null,
        supplier_id: form.type === "fournisseur" && form.supplier_id !== "none" ? form.supplier_id : null,
    carrier_id: form.type === "transporteur" && form.carrier_id !== "none" ? form.carrier_id : null,
        product_ref: form.product_ref,
        description: form.description,
      });
      if (check.has_duplicates) {
        setDuplicates(check.duplicates);
        setShowDuplicateAlert(true);
        setSaving(false);
        return;
      }
      const { data } = await api.post("/complaints", buildPayload(false));
      toast.success(`Litige ${data.number} créé`);
      onCreated(data);
    } catch (err) {
      toast.error(getAxiosErrorMessage(err));
      setSaving(false);
    }
  };

  const forceCreate = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/complaints", buildPayload(true));
      toast.success(`Litige ${data.number} créé`);
      onCreated(data);
    } catch (err) {
      toast.error(getAxiosErrorMessage(err));
    } finally {
      setSaving(false);
      setShowDuplicateAlert(false);
    }
  };

  return (
    <DialogContent className="max-w-2xl rounded-2xl">
      <DialogHeader>
        <DialogTitle className="font-heading">Nouveau litige</DialogTitle>
        <DialogDescription>
          Enregistrez une non-conformité. Une détection automatique de doublons sera effectuée.
        </DialogDescription>
      </DialogHeader>

      {showDuplicateAlert ? (
        <div className="space-y-4" data-testid="duplicate-alert">
          <div className="p-4 rounded-xl border border-amber-200 bg-amber-50">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-amber-100 text-amber-700">
                <AlertTriangle size={18} strokeWidth={1.8} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-amber-900">Litige(s) similaire(s) détecté(s)</div>
                <div className="text-sm text-amber-800 mt-0.5">
                  Nous avons trouvé {duplicates.length} litige(s) ouvert(s) qui ressemblent à celui-ci. Vérifiez avant de créer un doublon.
                </div>
              </div>
            </div>
          </div>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {duplicates.map((d) => (
              <div key={d.id} className="p-3 rounded-xl border border-slate-100 bg-white" data-testid={`duplicate-item-${d.id}`}>
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="text-sm font-bold text-slate-900 font-heading">{d.number}</span>
                  <ComplaintStatusBadge status={d.status} />
                  <SeverityBadge severity={d.severity} />
                  <span className="ml-auto text-xs font-semibold text-blue-700">
                    similarité {Math.round((d.match_score || 0) * 100)}%
                  </span>
                </div>
                <div className="text-xs text-slate-500 mb-1">
                  {/* Quatrième site d'affichage du tiers, oublié lors de la
                      correction des trois autres : un doublon de type Transport
                      annonçait « — » alors que `carrier_name` était disponible.
                      Il passe par la source unique comme les autres. */}
                  {(d.created_at || "").slice(0, 10)} · {nomConcerne(d)} · par {d.created_by_name || "—"}
                </div>
                <div className="text-sm text-slate-700 line-clamp-2">{d.description}</div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setShowDuplicateAlert(false)} data-testid="duplicate-back-btn">
              Retour au formulaire
            </Button>
            <Button
              type="button"
              onClick={forceCreate}
              disabled={saving}
              className="bg-amber-600 hover:bg-amber-700 text-white rounded-xl"
              data-testid="duplicate-force-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Créer quand même
            </Button>
          </DialogFooter>
        </div>
      ) : (
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {typesAutorises.length > 1 ? (
            <div>
              <Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => set("type", v)}>
                <SelectTrigger className="rounded-xl mt-1.5" data-testid="dialog-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {typesAutorises.map((t) => (
                    <SelectItem key={t} value={t}>{TYPE_LABELS[t]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div>
              <Label>Type</Label>
              <div
                className="mt-1.5 p-2.5 rounded-xl bg-blue-50 text-blue-800 text-sm font-medium border border-blue-200"
                data-testid="dialog-type-fixed"
              >
                {TYPE_LABELS[typesAutorises[0]] || "Client"}
              </div>
            </div>
          )}
          <div>
            <Label>Sous-type</Label>
            <Input
              value={form.sub_type}
              onChange={(e) => set("sub_type", e.target.value)}
              placeholder="ex. Produit non conforme"
              className="rounded-xl mt-1.5"
              data-testid="dialog-subtype-input"
            />
          </div>
          {form.type === "client" && (
            <div className="md:col-span-2">
              <Label>Client</Label>
              <Select value={form.client_id} onValueChange={(v) => set("client_id", v)}>
                <SelectTrigger className="rounded-xl mt-1.5" data-testid="dialog-client-select"><SelectValue placeholder="Choisir un client" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {clients.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          )}
          {form.type === "transporteur" && (
            <div className="md:col-span-2">
              <Label>Transporteur</Label>
              <Select value={form.carrier_id} onValueChange={(v) => set("carrier_id", v)}>
                <SelectTrigger className="rounded-xl mt-1.5" data-testid="dialog-carrier-select"><SelectValue placeholder="Choisir un transporteur" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {carriers.map((t) => (<SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>))}
                </SelectContent>
              </Select>
              {carriers.length === 0 ? (
                <p className="text-xs text-amber-700 mt-1.5">
                  Aucun transporteur enregistré. Créez la fiche depuis Fournisseurs
                  en choisissant le type « Transporteur ».
                </p>
              ) : null}
            </div>
          )}
          {form.type === "fournisseur" && (
            <div className="md:col-span-2">
              <Label>Fournisseur</Label>
              <Select value={form.supplier_id} onValueChange={(v) => set("supplier_id", v)}>
                <SelectTrigger className="rounded-xl mt-1.5" data-testid="dialog-supplier-select"><SelectValue placeholder="Choisir un fournisseur" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {suppliers.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label>Référence produit</Label>
            <Input
              value={form.product_ref}
              onChange={(e) => set("product_ref", e.target.value)}
              placeholder="ex. REF-12345"
              className="rounded-xl mt-1.5"
              data-testid="dialog-product-ref-input"
            />
          </div>
          <div>
            <Label>Gravité</Label>
            <Select value={form.severity} onValueChange={(v) => set("severity", v)}>
              <SelectTrigger className="rounded-xl mt-1.5" data-testid="dialog-severity-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="faible">Faible</SelectItem>
                <SelectItem value="moyenne">Moyenne</SelectItem>
                <SelectItem value="critique">Critique</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label>Tags (séparés par virgule)</Label>
            <Input
              value={form.tags}
              onChange={(e) => set("tags", e.target.value)}
              placeholder="étiquetage, lot"
              className="rounded-xl mt-1.5"
              data-testid="dialog-tags-input"
            />
          </div>
          <div className="md:col-span-2">
            <Label>Description</Label>
            <Textarea
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              required
              rows={4}
              placeholder="Décrivez précisément le litige…"
              className="rounded-xl mt-1.5"
              data-testid="dialog-description-input"
            />
          </div>
          <DialogFooter className="md:col-span-2 mt-2">
            <Button type="button" variant="ghost" onClick={onClose}>Annuler</Button>
            <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl" data-testid="dialog-save-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Enregistrer
            </Button>
          </DialogFooter>
        </form>
      )}
    </DialogContent>
  );
}
